import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Receipt, DollarSign, User, AlertCircle, CheckCircle, Banknote, Calendar, CreditCard, Building2, FileText } from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import useCustomerStore from '../../stores/useCustomerStore'
import useInvoiceStore from '../../stores/useInvoiceStore'
import api from '../../services/api'

/**
 * Create Receipt Page - NEW INDEPENDENT RECEIPT SYSTEM
 * إنشاء سند قبض مستقل (غير مرتبط بفاتورة محددة)
 *
 * النظام الجديد (ديسمبر 2025):
 * - السندات مستقلة تماماً وليست مرتبطة بفواتير محددة
 * - تقلل من إجمالي ديون العميل
 * - رقم تسلسلي تلقائي (RCP-20251214-000001)
 */
const CreateReceipt = () => {
  const navigate = useNavigate()
  const { customers, fetchCustomers } = useCustomerStore()
  const { paymentMethods, fetchPaymentMethods } = useInvoiceStore()

  // Helper function to format date as DD-MM-YY
  const formatDateToDDMMYY = (date) => {
    const d = new Date(date)
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = String(d.getFullYear()).slice(-2)
    return `${day}-${month}-${year}`
  }

  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [receiptDate, setReceiptDate] = useState(formatDateToDDMMYY(new Date()))
  const [referenceNumber, setReferenceNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [customerDebt, setCustomerDebt] = useState(0)

  // Load customers and payment methods on mount - FORCE FRESH DATA
  useEffect(() => {
    console.log('🔄 Loading customers and payment methods...')
    // Force fresh data fetch when component mounts
    fetchCustomers().then(() => {
      console.log('✅ Customers loaded:', customers.length)
    }).catch(err => {
      console.error('❌ Error loading customers:', err)
    })
    fetchPaymentMethods()
  }, []) // Empty deps array to run only on mount

  // Calculate customer debt when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      // Get customer debt from current_balance
      const debt = parseFloat(selectedCustomer.current_balance || selectedCustomer.currentBalance || 0)
      console.log('💰 Customer debt calculated:', {
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        current_balance: selectedCustomer.current_balance,
        currentBalance: selectedCustomer.currentBalance,
        calculatedDebt: debt
      })
      setCustomerDebt(debt)
      // Set amount to customer debt as default
      setAmount(debt.toFixed(3))
    } else {
      setCustomerDebt(0)
      setAmount('')
    }
  }, [selectedCustomer])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!selectedCustomer) {
      setError('يرجى اختيار العميل')
      return
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('يرجى إدخال مبلغ صحيح')
      return
    }

    if (!paymentMethod) {
      setError('يرجى اختيار طريقة الدفع')
      return
    }

    const receiptAmount = parseFloat(amount)

    // Validate amount doesn't exceed customer debt
    if (receiptAmount > customerDebt) {
      setError(`مبلغ السند (${receiptAmount.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك) يتجاوز إجمالي ديون العميل (${customerDebt.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك)`)
      return
    }

    setIsSaving(true)

    try {
      // Parse date in DD-MM-YY format to ISO string
      const [day, month, year] = receiptDate.split('-').map(Number)
      const fullYear = year < 100 ? 2000 + year : year
      const dateObj = new Date(fullYear, month - 1, day)

      // Find payment method ID from the selected value
      const selectedMethod = paymentMethods.find(m => m.value === paymentMethod)

      const response = await api.post('/mobile/receipts', {
        customer_id: selectedCustomer.id,
        amount: receiptAmount,
        payment_method_id: selectedMethod ? selectedMethod.id : null,
        receipt_date: dateObj.toISOString(),
        reference_number: referenceNumber || null,
        notes: notes || null
      })

      if (response.data.success) {
        console.log('✅ Receipt created successfully:', response.data)

        // Refresh customers data FIRST before showing success
        const refreshedCustomers = await fetchCustomers()
        console.log('✅ Customers data refreshed after receipt creation')

        // Update selected customer with fresh data
        if (refreshedCustomers && Array.isArray(refreshedCustomers)) {
          const updatedCustomer = refreshedCustomers.find(c => c.id === selectedCustomer.id)
          if (updatedCustomer) {
            setSelectedCustomer(updatedCustomer)
            const newDebt = parseFloat(updatedCustomer.current_balance || updatedCustomer.currentBalance || 0)
            setCustomerDebt(newDebt)
            console.log('✅ Customer debt updated:', { oldDebt: customerDebt, newDebt })
          }
        }

        // Show success modal
        setShowSuccessModal(true)

        // Navigate after 2 seconds
        setTimeout(() => {
          navigate('/invoices')
        }, 2000)
      } else {
        setError(response.data.message || 'فشل في إنشاء سند القبض')
      }
    } catch (err) {
      console.error('Receipt creation error:', err)
      setError(err.response?.data?.message || 'حدث خطأ أثناء إنشاء سند القبض')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 mb-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/invoices')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowRight className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              إنشاء سند قبض
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              سند قبض مستقل (غير مرتبط بفاتورة محددة)
            </p>
          </div>
        </div>
      </header>

      {/* Error Message */}
      {error && (
        <div className="mx-4 mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-4 space-y-4">
        {/* Customer Selection */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" />
            اختر العميل المديون
          </h3>

          {/* Customers with debt count */}
          {(() => {
            const customersWithDebt = customers.filter(c => (c.current_balance || c.currentBalance || 0) > 0)
            return (
              <>
                {customersWithDebt.length > 0 ? (
                  <>
                    <select
                      value={selectedCustomer?.id || ''}
                      onChange={(e) => {
                        const customer = customers.find(c => c.id == e.target.value)
                        setSelectedCustomer(customer)
                      }}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-base"
                      required
                    >
                      <option value="" className="text-gray-500">
                        -- اختر العميل ({customersWithDebt.length} عميل مديون) --
                      </option>
                      {customersWithDebt
                        .sort((a, b) => (b.current_balance || b.currentBalance || 0) - (a.current_balance || a.currentBalance || 0))
                        .map((customer) => {
                          const debt = customer.current_balance || customer.currentBalance || 0
                          const phone = customer.phone || customer.mobile || ''
                          return (
                            <option key={customer.id} value={customer.id} className="py-2">
                              {customer.name} • 📱 {phone} • 💰 {debt.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                            </option>
                          )
                        })}
                    </select>

                    {/* Info message */}
                    <div className="mt-2 flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>يظهر فقط العملاء الذين لديهم ديون مستحقة (مرتبة من الأعلى للأقل)</span>
                    </div>
                  </>
                ) : (
                  <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-center">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
                      لا توجد عملاء مديونين
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      جميع العملاء ليس لديهم ديون مستحقة
                    </p>
                  </div>
                )}
              </>
            )
          })()}

          {/* Customer Details Card */}
          {selectedCustomer && customerDebt > 0 && (
            <div className="mt-3 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800">
              {/* Customer Name & Phone */}
              <div className="mb-3 pb-3 border-b border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-bold text-gray-900 dark:text-white">
                    {selectedCustomer.name}
                  </span>
                </div>
                {(selectedCustomer.phone || selectedCustomer.mobile) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span>📱</span>
                    <span dir="ltr">{selectedCustomer.phone || selectedCustomer.mobile}</span>
                  </div>
                )}
              </div>

              {/* Debt Amount */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  📊 إجمالي الديون:
                </span>
                <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {customerDebt.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                </span>
              </div>

              {/* Max Receipt Amount */}
              <div className="pt-2 border-t border-blue-200 dark:border-blue-700">
                <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  الحد الأقصى لمبلغ السند: {customerDebt.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Amount */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            المبلغ
          </h3>

          <div className="relative">
            <input
              type="number"
              step="0.001"
              min="0"
              max={customerDebt}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.000"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
              required
              disabled={!selectedCustomer}
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              د.ك
            </span>
          </div>

          {amount && parseFloat(amount) > 0 && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              المتبقي بعد السند: {Math.max(0, customerDebt - parseFloat(amount)).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
            </p>
          )}
        </div>

        {/* Payment Method - Grid Style like AddPaymentModal */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            طريقة الدفع <span className="text-red-500">*</span>
          </h3>

          <div className="grid grid-cols-2 gap-2">
            {(() => {
              // Icon mapping for payment methods
              const iconMapping = {
                'cash': Banknote,
                'credit_card': CreditCard,
                'bank_transfer': Building2,
                'cheque': FileText,
                'e_wallet': CreditCard
              }

              // Color mapping for payment methods
              const colorMapping = {
                'cash': 'text-green-600 dark:text-green-400',
                'credit_card': 'text-blue-600 dark:text-blue-400',
                'bank_transfer': 'text-purple-600 dark:text-purple-400',
                'cheque': 'text-orange-600 dark:text-orange-400',
                'e_wallet': 'text-indigo-600 dark:text-indigo-400'
              }

              // Build payment methods list from API or fallback to hardcoded
              const paymentMethodsList = paymentMethods.length > 0
                ? paymentMethods.map(method => ({
                    value: method.value,
                    label: method.name,
                    icon: iconMapping[method.value] || CreditCard,
                    color: colorMapping[method.value] || 'text-gray-600 dark:text-gray-400'
                  }))
                : [
                    { value: 'cash', label: 'نقدي', icon: Banknote, color: 'text-green-600 dark:text-green-400' },
                    { value: 'bank_transfer', label: 'تحويل بنكي', icon: Building2, color: 'text-purple-600 dark:text-purple-400' },
                    { value: 'cheque', label: 'شيك', icon: FileText, color: 'text-orange-600 dark:text-orange-400' },
                    { value: 'credit_card', label: 'بطاقة', icon: CreditCard, color: 'text-blue-600 dark:text-blue-400' }
                  ]

              return paymentMethodsList.map((method) => {
                const Icon = method.icon
                return (
                  <label
                    key={method.value}
                    className={`
                      flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all
                      ${paymentMethod === method.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.value}
                      checked={paymentMethod === method.value}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="sr-only"
                    />
                    <Icon className={`w-5 h-5 ${method.color}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {method.label}
                      </p>
                    </div>
                  </label>
                )
              })
            })()}
          </div>
        </div>

        {/* Receipt Date - DatePicker like AddPaymentModal */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            تاريخ السند <span className="text-red-500">*</span>
          </h3>

          <div className="relative">
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
            <DatePicker
              selected={receiptDate ? (() => {
                // Convert DD-MM-YY to Date object
                const match = receiptDate.match(/^(\d{2})-(\d{2})-(\d{2})$/)
                if (match) {
                  const [, day, month, year] = match
                  const fullYear = parseInt(year) < 100 ? 2000 + parseInt(year) : parseInt(year)
                  return new Date(fullYear, parseInt(month) - 1, parseInt(day))
                }
                return new Date()
              })() : new Date()}
              onChange={(date) => {
                if (date) {
                  const day = String(date.getDate()).padStart(2, '0')
                  const month = String(date.getMonth() + 1).padStart(2, '0')
                  const year = String(date.getFullYear()).slice(-2)
                  setReceiptDate(`${day}-${month}-${year}`)
                }
              }}
              dateFormat="dd-MM-yy"
              maxDate={new Date()}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={10}
              placeholderText="اختر التاريخ"
              className="w-full pr-10 pl-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer"
              wrapperClassName="w-full"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            التنسيق: DD-MM-YY (يوم-شهر-سنة)
          </p>
        </div>

        {/* Reference Number (Optional) */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            رقم المرجع (اختياري)
          </h3>

          <input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            placeholder="رقم الشيك، رقم التحويل، إلخ..."
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Notes (Optional) */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            ملاحظات (اختياري)
          </h3>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="أضف ملاحظات إضافية..."
            rows="3"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Submit Button */}
        <div className="pb-safe-bottom">
          <button
            type="submit"
            disabled={isSaving || !selectedCustomer || !amount || !paymentMethod}
            className="btn btn-primary w-full flex items-center justify-center gap-2 touch-target disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>جاري الحفظ...</span>
              </>
            ) : (
              <>
                <Receipt className="w-5 h-5" />
                <span>إنشاء سند القبض</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-2xl transform animate-scaleIn">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-3">
              تم إنشاء سند القبض بنجاح!
            </h2>

            {/* Message */}
            <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
              تم تقليل رصيد العميل بمبلغ السند
            </p>

            {/* Details */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">العميل:</span>
                <span className="font-semibold text-gray-900 dark:text-white">{selectedCustomer?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">المبلغ:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {parseFloat(amount).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">الرصيد المتبقي:</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {Math.max(0, customerDebt - parseFloat(amount || 0)).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
                </span>
              </div>
            </div>

            {/* Auto redirect message */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
              جاري التحويل لصفحة الفواتير...
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreateReceipt
